// AppContext.js
import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from 'react';
import { getFromDB, saveToDB } from './idb';
import secureStorage from 'hooks/secureStorage';

const AppContext = createContext();

export const AppProvider = ({ children }) => {
  const [cenquete, setCEnqueteState] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const isInitialized = useRef(false);

  // On mount: try to load from IndexedDB first (persisted changes),
  // fall back to secureStorage (original data) only if IndexedDB is empty
  useEffect(() => {
    const loadData = async () => {
      try {
        const storageEnquete = secureStorage.getItem('enquete_courante');
        const parsedStorage = storageEnquete
          ? JSON.parse(storageEnquete)
          : null;

        // First, try to get persisted data from IndexedDB
        const dbData = await getFromDB('current_enquete');
        if (dbData) {
          // Check if the user switched to a different enquête
          const dbNumero = dbData?.numero_enquete;
          const storageNumero = parsedStorage?.numero_enquete;
          if (storageNumero && dbNumero !== storageNumero) {
            // Different enquête selected — use fresh data from secureStorage
            console.log('Enquête changed, reloading from secureStorage');
            await saveToDB('current_enquete', parsedStorage);
            setCEnqueteState(parsedStorage);
            isInitialized.current = true;
            setIsLoading(false);
            return;
          }

          console.log('enquete loaded from IndexedDB', dbData);
          setCEnqueteState(dbData);
          isInitialized.current = true;
          setIsLoading(false);
          return;
        }

        // Fallback: load from secureStorage (original enquete data)
        if (parsedStorage) {
          console.log('enquete loaded from secureStorage', parsedStorage);
          await saveToDB('current_enquete', parsedStorage);
          setCEnqueteState(parsedStorage);
        }
      } catch (error) {
        console.error('Error loading enquete data:', error);
        // Fallback to secureStorage on error
        const storageEnquete = secureStorage.getItem('enquete_courante');
        if (storageEnquete) {
          setCEnqueteState(JSON.parse(storageEnquete));
        }
      }
      isInitialized.current = true;
      setIsLoading(false);
    };
    loadData();
  }, []);

  // Wrapper around setCEnquete that also persists to IndexedDB
  const setCEnquete = useCallback((valueOrUpdater) => {
    setCEnqueteState((prev) => {
      const newValue =
        typeof valueOrUpdater === 'function'
          ? valueOrUpdater(prev)
          : valueOrUpdater;

      // Persist to IndexedDB asynchronously
      if (newValue && isInitialized.current) {
        saveToDB('current_enquete', newValue).catch((err) =>
          console.error('Error persisting cenquete to IndexedDB:', err)
        );
      }

      return newValue;
    });
  }, []);

  return (
    <AppContext.Provider value={{ cenquete, setCEnquete, isLoading }}>
      {!isLoading && children}
    </AppContext.Provider>
  );
};

export const useAppContext = () => useContext(AppContext);
